import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Container, Typography, Paper, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Card, CardActionArea, CardContent, Grid, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Tooltip, Divider, Tabs, Tab, Alert, AlertTitle, Skeleton, Switch,
  ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Refresh as RefreshIcon,
  PowerSettingsNew as PowerIcon,
  WarningAmber as WarningIcon,
  PlayCircleOutline as RunningIcon,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

import adminAxios from '../../../adminApi/adminApi';
import CustomPagination from '../../../common/custom-pagination';
import { ColorsConstants, borderColors, dashboardCardBgColor } from '../../../cssUtils/constants';

const CRON_API = '/cron-jobs';
const AUTO_REFRESH_MS = 60000;

const STATUS = {
  SUCCESS:  { label: 'Success',  color: 'success'   },
  PARTIAL:  { label: 'Partial',  color: 'warning'   },
  FAILED:   { label: 'Failed',   color: 'error'     },
  RUNNING:  { label: 'Running',  color: 'info'      },
  OVERDUE:  { label: 'Overdue',  color: 'secondary' },
  DISABLED: { label: 'Disabled', color: 'default'   },
};
// Health only exists per cron (latest view); runs have the first four
const LATEST_STATUSES = ['SUCCESS', 'PARTIAL', 'FAILED', 'OVERDUE', 'RUNNING', 'DISABLED'];
const RUN_STATUSES = ['SUCCESS', 'PARTIAL', 'FAILED', 'RUNNING'];
const NEEDS_ATTENTION = ['FAILED', 'PARTIAL', 'OVERDUE'];
const TIME_RANGES = [
  { value: '1h', label: 'Last 1 hour' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: 'custom', label: 'Custom' },
];
const DEFAULT_FILTERS = { search: '', status: '', cronName: '', range: '24h', start: '', end: '', attentionOnly: false };

const buildQuery = (params) =>
  new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    )
  ).toString();

const fmtDatetime = (iso) => {
  if (!iso) return '—';
  const date = new Date(iso);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day}/${month}/${date.getFullYear()} ${hours}:${minutes} ${ampm}`;
};
const fmtTime = (iso) => fmtDatetime(iso).split(' ').slice(1).join(' ');
const timeAgo = (iso) => {
  if (!iso) return '';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};
const everyLabel = (minutes) => {
  if (!minutes) return 'Schedule unknown';
  if (minutes < 60) return `Every ${minutes} min`;
  if (minutes < 1440) return `Every ${minutes / 60} h`;
  if (minutes < 10080) return 'Daily';
  return minutes < 44640 ? 'Weekly' : 'Monthly';
};

const StatusChip = ({ status }) => {
  const cfg = STATUS[status] || { label: status, color: 'default' };
  return <Chip label={cfg.label} size="small" color={cfg.color} />;
};

// ── Enabled / Disabled badge ───────────────────────────────────────────────
const EnabledBadge = ({ enabled, schedule }) => {
  const cfg =
    enabled === true
      ? { label: 'Enabled',  color: 'success', variant: 'filled'   }
      : enabled === false
      ? { label: 'Disabled', color: 'error',   variant: 'filled'   }
      : { label: 'Unknown',  color: 'default', variant: 'outlined' };
  return (
    <Tooltip title={`Schedule: ${schedule || 'not found in CRONJOBS'}`} arrow>
      <span>
        <Chip size="small" icon={<PowerIcon sx={{ fontSize: 13 }} />} label={cfg.label}
          color={cfg.color} variant={cfg.variant} sx={{ fontWeight: 600 }} />
      </span>
    </Tooltip>
  );
};

const SkeletonRows = ({ cols, rows = 6 }) =>
  Array.from({ length: rows }).map((_, i) => (
    <TableRow key={i}>
      {Array.from({ length: cols }).map((__, j) => (
        <TableCell key={j}><Skeleton variant="text" width={j === 0 ? '80%' : '60%'} /></TableCell>
      ))}
    </TableRow>
  ));

const TenantsCell = ({ run }) => {
  if (!run.total_tenants) return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (run.status === 'RUNNING') return <Chip label={`${run.total_tenants} in progress`} size="small" variant="outlined" color="primary" />;
  const ok = run.total_tenants - (run.failed_tenants || 0);
  return (
    <Chip label={`${ok}/${run.total_tenants} ok`} size="small" variant="outlined"
      color={run.failed_tenants ? 'error' : 'primary'} />
  );
};

// Last runs as small colored bars, oldest → newest
const RunStrip = ({ runs, slots = 10 }) => {
  const colors = { SUCCESS: 'success.main', PARTIAL: 'warning.main', FAILED: 'error.main', RUNNING: 'info.main' };
  return (
    <Box sx={{ display: 'inline-flex', gap: '3px' }} aria-label={`Last ${runs.length} runs, oldest to newest`}>
      {Array.from({ length: slots - runs.length }).map((_, i) => (
        <Box key={`pad-${i}`} sx={{ width: 8, height: 16, borderRadius: '2px', bgcolor: 'grey.300' }} />
      ))}
      {runs.map((run) => (
        <Tooltip key={run.cron_execution_id} title={`${fmtDatetime(run.start_time)} · ${STATUS[run.status]?.label || run.status}`} arrow>
          <Box sx={{ width: 8, height: 16, borderRadius: '2px', bgcolor: colors[run.status] || 'grey.400' }} />
        </Tooltip>
      ))}
    </Box>
  );
};

const SuccessRate = ({ rate }) => {
  if (rate === null || rate === undefined) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const color = rate < 0.9 ? 'error.main' : rate < 0.98 ? 'warning.main' : 'success.main';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 56, height: 6, borderRadius: 3, bgcolor: 'grey.300', overflow: 'hidden' }}>
        <Box sx={{ width: `${Math.round(rate * 100)}%`, height: '100%', bgcolor: color }} />
      </Box>
      <Typography variant="body2">{(rate * 100).toFixed(1)}%</Typography>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────
const CronMonitoringDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const cronType = activeTab === 0 ? 'SYSTEM' : 'CUSTOMER';
  const [view, setView] = useState('latest'); // latest | all

  // ── Filters ───────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // ── Data ──────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null);
  const [latestRows, setLatestRows] = useState([]);
  const [groups, setGroups] = useState({ results: [], count: 0, executions: 0 });
  const [cronNameCounts, setCronNameCounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [, setClock] = useState(0); // re-renders the "updated … ago" text

  // ── Row UI state ──────────────────────────────────────────────────────────
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [openErrors, setOpenErrors] = useState(() => new Set());
  const [copiedId, setCopiedId] = useState(null);

  // ── Tenant breakdown dialog ───────────────────────────────────────────────
  const [selectedRun, setSelectedRun] = useState(null);
  const [tenantRows, setTenantRows] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(false);
  const [tenantError, setTenantError] = useState(null);
  const [tenantFilter, setTenantFilter] = useState('');
  const [failedOnly, setFailedOnly] = useState(false);

  // Only the newest request may update the table, so a slow older one can't show stale rows
  const latestRequest = useRef(0);

  const updateFilters = (patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(0);
  };

  // Search waits until typing pauses
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => (prev.search === searchInput.trim() ? prev : { ...prev, search: searchInput.trim() }));
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Fetching ──────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    setError(null);
    try {
      const typeQuery = buildQuery({ cron_type: cronType });
      const requests = [
        adminAxios.get(`${CRON_API}/stats/?${typeQuery}`),
        adminAxios.get(`${CRON_API}/dashboard/?${buildQuery({ cron_type: cronType, view: 'latest' })}`),
        adminAxios.get(`${CRON_API}/filters/?${typeQuery}`),
      ];
      if (view === 'all') {
        requests.push(adminAxios.get(`${CRON_API}/dashboard/?${buildQuery({
          cron_type: cronType,
          group: 1,
          status: filters.status || undefined,
          cron_name: filters.cronName || undefined,
          search: filters.search || undefined,
          range: filters.range === 'custom' ? undefined : filters.range,
          start_date: filters.range === 'custom' ? filters.start : undefined,
          end_date: filters.range === 'custom' ? filters.end : undefined,
          page: page + 1,
          page_size: rowsPerPage,
        })}`));
      }
      const [statsRes, latestRes, filtersRes, groupsRes] = await Promise.all(requests);
      if (requestId !== latestRequest.current) return;
      setStats(statsRes.data);
      setLatestRows(latestRes.data.results || []);
      setCronNameCounts(filtersRes.data.cron_name_counts || []);
      if (groupsRes) setGroups(groupsRes.data);
      setLastUpdated(new Date());
    } catch (err) {
      if (err.message === 'Token expired' || err.message === 'No access token') return; // handled by adminAxios
      if (requestId === latestRequest.current) setError(err.response?.data?.error || 'Failed to load cron jobs.');
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [cronType, view, filters, page, rowsPerPage]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (autoRefresh) fetchData();
    }, AUTO_REFRESH_MS);
    const clock = setInterval(() => setClock((n) => n + 1), 15000);
    return () => { clearInterval(timer); clearInterval(clock); };
  }, [autoRefresh, fetchData]);

  const fetchTenantBreakdown = useCallback(async (run) => {
    setTenantLoading(true);
    setTenantError(null);
    try {
      const { data } = await adminAxios.get(`${CRON_API}/history/?${buildQuery({ cron_execution_id: run.cron_execution_id, page_size: 100 })}`);
      const rows = data.results || [];
      setTenantRows(rows);
      setFailedOnly(rows.some((r) => r.status === 'FAILED'));
    } catch (err) {
      if (err.message === 'Token expired' || err.message === 'No access token') return;
      setTenantError('Failed to load tenant breakdown.');
    } finally {
      setTenantLoading(false);
    }
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleTabChange = (_, value) => {
    setActiveTab(value);
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setPage(0);
    setExpandedGroups(new Set());
    setOpenErrors(new Set());
  };

  const handleViewChange = (_, value) => {
    if (!value) return;
    setView(value);
    const allowed = value === 'latest' ? LATEST_STATUSES : RUN_STATUSES;
    updateFilters({ status: allowed.includes(filters.status) ? filters.status : '', attentionOnly: false });
  };

  const clearAll = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchInput('');
    setPage(0);
  };

  // A cron's run history: All executions, filtered to that cron over 7 days
  const showCronRuns = (cronName) => {
    setView('all');
    setSearchInput('');
    setFilters({ ...DEFAULT_FILTERS, cronName, range: '7d' });
    setPage(0);
  };

  const handleCard = (key) => {
    setSearchInput('');
    setPage(0);
    if (key === 'all') { setView('latest'); setFilters(DEFAULT_FILTERS); }
    if (key === 'attention') { setView('latest'); setFilters({ ...DEFAULT_FILTERS, attentionOnly: true }); }
    if (key === 'running') { setView('latest'); setFilters({ ...DEFAULT_FILTERS, status: 'RUNNING' }); }
    if (key === 'success') { setView('all'); setFilters({ ...DEFAULT_FILTERS, status: 'SUCCESS' }); }
    if (key === 'failed') { setView('all'); setFilters({ ...DEFAULT_FILTERS, status: 'FAILED' }); }
  };

  const toggleSet = (setter, key) =>
    setter((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const copyError = async (run) => {
    try {
      await navigator.clipboard.writeText(run.error_message);
      setCopiedId(run.cron_execution_id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      setCopiedId(`fail-${run.cron_execution_id}`);
    }
  };

  const openBreakdown = (run) => {
    setSelectedRun(run);
    setTenantFilter('');
    setTenantRows([]);
    fetchTenantBreakdown(run);
  };
  const closeBreakdown = () => {
    setSelectedRun(null);
    setTenantRows([]);
    setTenantFilter('');
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const needsAttention = useMemo(() => latestRows.filter((r) => NEEDS_ATTENTION.includes(r.health)), [latestRows]);

  // The latest view has one row per cron, so it filters in the browser
  const visibleLatest = useMemo(() => {
    const q = filters.search.toLowerCase();
    return latestRows.filter((r) =>
      (!filters.status || r.health === filters.status)
      && (!filters.attentionOnly || NEEDS_ATTENTION.includes(r.health))
      && (!filters.cronName || r.cron_name === filters.cronName)
      && (!q || r.cron_name.toLowerCase().includes(q) || (r.error_message || '').toLowerCase().includes(q)));
  }, [latestRows, filters]);

  const activeFilterChips = [
    filters.attentionOnly && view === 'latest' && { key: 'attentionOnly', label: 'Needs attention', clear: { attentionOnly: false } },
    filters.search && { key: 'search', label: `Search: “${filters.search}”`, clear: { search: '' } },
    filters.status && { key: 'status', label: `Status: ${STATUS[filters.status].label}`, clear: { status: '' } },
    filters.cronName && { key: 'cronName', label: `Cron: ${filters.cronName}`, clear: { cronName: '' } },
    view === 'all' && filters.range !== '24h' && {
      key: 'range',
      label: filters.range === 'custom'
        ? `Dates: ${filters.start || '…'} → ${filters.end || '…'}`
        : `Range: ${TIME_RANGES.find((t) => t.value === filters.range).label}`,
      clear: { range: '24h', start: '', end: '' },
    },
  ].filter(Boolean);

  const shownTenants = useMemo(() => {
    let rows = tenantFilter ? tenantRows.filter((r) => r.tenant_id === tenantFilter) : tenantRows;
    if (failedOnly) rows = rows.filter((r) => r.status === 'FAILED');
    return rows;
  }, [tenantRows, tenantFilter, failedOnly]);
  const failedTenantCount = tenantRows.filter((r) => r.status === 'FAILED').length;

  const statusOptions = view === 'latest' ? LATEST_STATUSES : RUN_STATUSES;
  const typeLabel = cronType === 'SYSTEM' ? 'System' : 'Customer';

  // ── Render helpers ────────────────────────────────────────────────────────
  const errorLine = (run) => run.error_message && (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: 220, mt: 0.5 }}>
      <Typography variant="caption" color="error" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
        {run.error_message}
      </Typography>
      <Button size="small" sx={{ minWidth: 0, px: 0.5, py: 0, fontSize: 11 }}
        onClick={() => toggleSet(setOpenErrors, run.cron_execution_id)}>
        {openErrors.has(run.cron_execution_id) ? 'Hide' : 'Show'}
      </Button>
    </Box>
  );

  const errorRow = (run, cols) => (!run.error_message || !openErrors.has(run.cron_execution_id) ? null : (
    <TableRow key={`err-${run.cron_execution_id}`}>
      <TableCell colSpan={cols} sx={{ bgcolor: '#FDEDED', py: 1.25 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Typography component="code" sx={{ color: '#5F2120', fontSize: 13, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', flex: 1, minWidth: 0 }}>
            {run.error_message}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => copyError(run)}>
            {copiedId === run.cron_execution_id ? 'Copied' : copiedId === `fail-${run.cron_execution_id}` ? 'Select the text to copy' : 'Copy error'}
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  ));

  const breakdownButton = (run) => (run.total_tenants > 0
    ? (
      <Tooltip title="View tenant breakdown">
        <IconButton size="small" color="primary" onClick={() => openBreakdown(run)}><VisibilityIcon /></IconButton>
      </Tooltip>
    )
    : <Typography variant="body2" color="text.secondary" component="span">—</Typography>);

  const runCells = (run) => (
    <>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="body2">{fmtDatetime(run.start_time)}</Typography></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="body2">{fmtDatetime(run.end_time)}</Typography></TableCell>
      <TableCell><StatusChip status={run.status} />{errorLine(run)}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="body2">{run.duration_display || '—'}</Typography></TableCell>
      <TableCell><TenantsCell run={run} /></TableCell>
      <TableCell>
        {run.total_records != null
          ? <Chip label={run.total_records.toLocaleString()} size="small" color="success" variant="outlined" />
          : <Typography variant="body2" color="text.secondary">—</Typography>}
      </TableCell>
      <TableCell><EnabledBadge enabled={run.enabled} schedule={run.schedule} /></TableCell>
      <TableCell align="center">{breakdownButton(run)}</TableCell>
    </>
  );

  const renderGroup = (group) => {
    const run = group.latest;
    if (group.run_count === 1) {
      return [
        <TableRow key={group.key} hover>
          <TableCell>
            <Typography variant="body2" fontWeight="medium">{run.cron_name}</Typography>
            <Typography variant="caption" color="text.secondary">{run.environment}</Typography>
          </TableCell>
          {runCells(run)}
        </TableRow>,
        errorRow(run, 9),
      ];
    }
    const open = expandedGroups.has(group.key);
    return [
      <TableRow key={group.key} hover sx={open ? { '& > td': { bgcolor: '#F5F8FE' } } : undefined}>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, ml: -1 }}>
            <IconButton size="small" onClick={() => toggleSet(setExpandedGroups, group.key)}
              aria-expanded={open} aria-label={`${open ? 'Collapse' : 'Expand'} ${group.run_count} runs`}>
              <ExpandMoreIcon sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </IconButton>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {run.cron_name}{' '}
                <Chip label={`${group.run_count.toLocaleString()} runs`} size="small" variant="outlined" sx={{ height: 20, fontSize: 11, ml: 0.5 }} />
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {run.environment} · {fmtTime(group.first_start)} – {fmtTime(group.last_start)}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        {runCells(run)}
      </TableRow>,
      errorRow(run, 9),
      ...(open ? group.runs.flatMap((child, i) => [
        <TableRow key={`${group.key}-${child.cron_execution_id}`} hover
          sx={{ '& > td': { bgcolor: '#FBFCFF', py: 1.1 }, '& > td:first-of-type': { borderLeft: `3px solid ${ColorsConstants.primaryPurple110 || '#1976D2'}` } }}>
          <TableCell>
            <Box sx={{ pl: 4.25 }}>
              <Typography variant="body2">{i === 0 ? 'Latest run' : `Run ${i + 1}`}</Typography>
              <Typography variant="caption" color="text.secondary">#{child.cron_execution_id}</Typography>
            </Box>
          </TableCell>
          {runCells(child)}
        </TableRow>,
        i > 0 ? errorRow(child, 9) : null,
      ]) : []),
      open && group.run_count > group.runs.length ? (
        <TableRow key={`${group.key}-more`}>
          <TableCell colSpan={9} sx={{ bgcolor: '#FBFCFF', py: 1, pl: 6.25 }}>
            <Typography variant="caption" color="text.secondary">
              Showing the latest {group.runs.length} of {group.run_count.toLocaleString()} runs in this row
            </Typography>
          </TableCell>
        </TableRow>
      ) : null,
    ];
  };

  const cards = [
    { key: 'all', label: `${typeLabel} Cron Jobs`, value: latestRows.length, color: 'primary.main', Icon: TimelineIcon, hint: 'Show every cron' },
    { key: 'attention', label: 'Needs attention', value: needsAttention.length, color: needsAttention.length ? 'warning.main' : 'success.main', Icon: WarningIcon, hint: 'Failed, partial or overdue' },
    { key: 'running', label: 'Running now', value: latestRows.filter((r) => r.health === 'RUNNING').length, color: 'info.main', Icon: RunningIcon, hint: 'Show running crons' },
    { key: 'success', label: 'Successful (24h)', value: stats?.successful_last_24h, color: 'success.main', Icon: CheckCircleIcon, hint: 'Show these runs' },
    { key: 'failed', label: 'Failed (24h)', value: stats?.failed_last_24h, color: 'error.main', Icon: CancelIcon, hint: `+ ${stats?.partial_last_24h ?? 0} partial · show these runs` },
  ];

  const attentionReason = (row) => {
    if (row.health === 'FAILED') return `last run failed ${timeAgo(row.start_time)}${row.error_message ? ` — ${row.error_message}` : ''}`;
    if (row.health === 'PARTIAL') return `${row.failed_tenants} of ${row.total_tenants} tenants failed in the last run (${timeAgo(row.start_time)})`;
    return `no run since ${fmtDatetime(row.start_time)}; scheduled ${everyLabel(row.interval_minutes).toLowerCase()}`;
  };

  const showEmpty = !loading && !error && (view === 'latest' ? visibleLatest.length === 0 : groups.results.length === 0);

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">Cron Job Monitoring Dashboard</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Track cron job health and execution history across system and customer crons
          </Typography>
        </Box>

        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab icon={<SettingsIcon />} iconPosition="start" label="System Crons" />
            <Tab icon={<PeopleIcon />} iconPosition="start" label="Customer Crons" />
          </Tabs>
        </Paper>

        {/* Summary cards — each one is a filter */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {cards.map(({ key, label, value, color, Icon, hint }) => (
            <Grid item xs={12} sm={6} md key={key}>
              <Card sx={{ bgcolor: dashboardCardBgColor, height: '100%' }}>
                <CardActionArea onClick={() => handleCard(key)} sx={{ height: '100%' }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="h4" fontWeight="bold" color={color}>
                        {loading && value === undefined ? <Skeleton width={40} /> : (value ?? '—')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{hint}</Typography>
                    </Box>
                    <Icon sx={{ fontSize: 40, color, opacity: 0.3 }} />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Needs attention */}
        {!loading || latestRows.length ? (
          needsAttention.length ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>{needsAttention.length} cron{needsAttention.length > 1 ? 's need' : ' needs'} attention</AlertTitle>
              <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {needsAttention.map((row) => (
                  <li key={row.cron_name}>
                    <Button onClick={() => showCronRuns(row.cron_name)} color="inherit"
                      sx={{ textAlign: 'left', justifyContent: 'flex-start', p: 0, fontWeight: 400, textTransform: 'none', gap: 1 }}>
                      <StatusChip status={row.health} />
                      <span><strong>{row.cron_name}</strong> — {attentionReason(row)}</span>
                    </Button>
                  </li>
                ))}
              </Box>
            </Alert>
          ) : latestRows.length > 0 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Every {typeLabel.toLowerCase()} cron ran on schedule and its last run succeeded.
            </Alert>
          )
        ) : null}

        {/* Main table */}
        <Paper>
          <Box sx={{ p: 2, bgcolor: dashboardCardBgColor, borderBottom: `1px solid ${borderColors.cardBorderPrimaryGrayColor}` }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {cronType === 'SYSTEM' ? '⚙️ System Cron Jobs' : '👥 Customer Cron Jobs'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {view === 'latest'
                    ? 'One row per cron — its latest run, recent history and health'
                    : 'Runs of the same cron are grouped until the status changes; expand a row for its latest 10 runs'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <ToggleButtonGroup size="small" exclusive value={view} onChange={handleViewChange} sx={{ bgcolor: 'background.paper' }}>
                  <ToggleButton value="latest">Latest per cron</ToggleButton>
                  <ToggleButton value="all">All executions</ToggleButton>
                </ToggleButtonGroup>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)}
                    inputProps={{ 'aria-label': 'Auto-refresh every 60 seconds' }} />
                  <Typography variant="caption" color="text.secondary">
                    Auto-refresh · {lastUpdated ? `updated ${timeAgo(lastUpdated)}` : 'loading'}
                  </Typography>
                  <Tooltip title="Refresh now">
                    <IconButton size="small" onClick={fetchData}><RefreshIcon /></IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>

            {/* Filters */}
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} md={4}>
                <TextField fullWidth size="small" placeholder="Search cron jobs or errors…"
                  value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={filters.status} label="Status" onChange={(e) => updateFilters({ status: e.target.value })}>
                    <MenuItem value="">All Status</MenuItem>
                    {statusOptions.map((s) => <MenuItem key={s} value={s}>{STATUS[s].label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel>Cron Name</InputLabel>
                  <Select value={filters.cronName} label="Cron Name" onChange={(e) => updateFilters({ cronName: e.target.value })}>
                    <MenuItem value="">All Cron Jobs ({cronNameCounts.length})</MenuItem>
                    {cronNameCounts.map((c) => (
                      <MenuItem key={c.cron_name} value={c.cron_name}>{c.cron_name} · {c.runs.toLocaleString()} runs</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {view === 'all' && (
              <>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Time range</Typography>
                  {TIME_RANGES.map((t) => (
                    <Chip key={t.value} label={t.label} size="small" clickable
                      color={filters.range === t.value ? 'primary' : 'default'}
                      variant={filters.range === t.value ? 'filled' : 'outlined'}
                      onClick={() => updateFilters({ range: t.value })} />
                  ))}
                </Box>
                {filters.range === 'custom' && (
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5 }}>
                    <TextField size="small" label="Start Date" type="date" value={filters.start}
                      onChange={(e) => updateFilters({ start: e.target.value })} InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="End Date" type="date" value={filters.end}
                      onChange={(e) => updateFilters({ end: e.target.value })} InputLabelProps={{ shrink: true }} />
                  </Box>
                )}
              </>
            )}

            {activeFilterChips.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1.5 }}>
                {activeFilterChips.map((c) => (
                  <Chip key={c.key} label={c.label} size="small" color="primary" variant="outlined"
                    onDelete={() => {
                      if (c.key === 'search') setSearchInput('');
                      updateFilters(c.clear);
                    }} />
                ))}
                <Button size="small" onClick={clearAll}>Clear all</Button>
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {view === 'latest'
                ? `Showing ${visibleLatest.length} of ${latestRows.length} crons · sorted by what needs attention first`
                : `Showing ${groups.results.length} of ${groups.count} rows · ${groups.executions ?? 0} executions`}
            </Typography>
          </Box>

          {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

          <TableContainer>
            {view === 'latest' ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Cron Name</strong></TableCell>
                    <TableCell><strong>Health</strong></TableCell>
                    <TableCell><strong>Last Run</strong></TableCell>
                    <TableCell><strong>Duration</strong></TableCell>
                    <TableCell><strong>Last 10 Runs</strong></TableCell>
                    <TableCell><strong>Success (7d)</strong></TableCell>
                    <TableCell><strong>Tenants (last run)</strong></TableCell>
                    <TableCell><strong>Scheduler</strong></TableCell>
                    <TableCell align="center"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !latestRows.length
                    ? <SkeletonRows cols={9} />
                    : visibleLatest.flatMap((row) => [
                      <TableRow key={row.cron_name} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{row.cron_name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {everyLabel(row.interval_minutes)}{row.schedule ? ` · ${row.schedule}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell><StatusChip status={row.health} />{errorLine(row)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{fmtDatetime(row.start_time)}</Typography>
                          <Typography variant="caption" color="text.secondary">{timeAgo(row.start_time)}</Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.duration_display || '—'}</TableCell>
                        <TableCell><RunStrip runs={row.recent || []} /></TableCell>
                        <TableCell><SuccessRate rate={row.success_rate_7d} /></TableCell>
                        <TableCell><TenantsCell run={row} /></TableCell>
                        <TableCell><EnabledBadge enabled={row.enabled} schedule={row.schedule} /></TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title="See all runs of this cron">
                            <IconButton size="small" color="primary" onClick={() => showCronRuns(row.cron_name)}><HistoryIcon /></IconButton>
                          </Tooltip>
                          {breakdownButton(row)}
                        </TableCell>
                      </TableRow>,
                      errorRow(row, 9),
                    ])}
                </TableBody>
              </Table>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Cron Name</strong></TableCell>
                    <TableCell><strong>Start Time</strong></TableCell>
                    <TableCell><strong>End Time</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Duration</strong></TableCell>
                    <TableCell><strong>Tenants</strong></TableCell>
                    <TableCell><strong>Records</strong></TableCell>
                    <TableCell><strong>Scheduler</strong></TableCell>
                    <TableCell align="center"><strong>Breakdown</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && !groups.results.length ? <SkeletonRows cols={9} /> : groups.results.flatMap(renderGroup)}
                </TableBody>
              </Table>
            )}
          </TableContainer>

          {showEmpty && (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <ErrorIcon sx={{ fontSize: 48, color: ColorsConstants.primaryGray30, mb: 2 }} />
              <Typography variant="h6" color="text.secondary">No cron jobs match these filters</Typography>
              <Typography variant="body2" color="text.secondary">Clear a filter or widen the time range.</Typography>
              {activeFilterChips.length > 0 && <Button variant="outlined" sx={{ mt: 2 }} onClick={clearAll}>Clear all filters</Button>}
            </Box>
          )}

          {view === 'all' && groups.count > 0 && (
            <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
              <CustomPagination
                count={groups.count}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(e.target.value); setPage(0); }}
              />
            </Box>
          )}
        </Paper>

        {/* ── Tenant Breakdown ─────────────────────────────────────────────── */}
        <Dialog open={Boolean(selectedRun)} onClose={closeBreakdown} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h6">{selectedRun?.cron_name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Tenant breakdown · {fmtDatetime(selectedRun?.start_time)}
                  {selectedRun ? ` · ${STATUS[selectedRun.status]?.label || selectedRun.status}` : ''}
                </Typography>
              </Box>
              <IconButton onClick={closeBreakdown} size="small" aria-label="Close"><CloseIcon /></IconButton>
            </Box>
          </DialogTitle>
          <Divider />

          <DialogContent>
            {tenantError && <Alert severity="error" sx={{ mb: 2 }}>{tenantError}</Alert>}

            {!tenantLoading && tenantRows.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                <FormControl size="small" sx={{ width: 280, maxWidth: '100%' }}>
                  <InputLabel>Filter by Tenant</InputLabel>
                  <Select value={tenantFilter} label="Filter by Tenant" onChange={(e) => setTenantFilter(e.target.value)}>
                    <MenuItem value="">All Tenants ({tenantRows.length})</MenuItem>
                    {tenantRows.filter((r) => r.tenant_id).map((r) => (
                      <MenuItem key={r.tenant_id} value={r.tenant_id}>{r.tenant_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={<Checkbox checked={failedOnly} disabled={!failedTenantCount} onChange={(e) => setFailedOnly(e.target.checked)} />}
                  label={`Failed only (${failedTenantCount})`}
                />
                <Chip label={`${tenantRows.length - failedTenantCount} succeeded`} size="small" color="success" variant="outlined" />
                {failedTenantCount > 0 && <Chip label={`${failedTenantCount} failed`} size="small" color="error" variant="outlined" />}
              </Box>
            )}

            {tenantLoading
              ? <Table size="small"><TableBody><SkeletonRows cols={7} rows={5} /></TableBody></Table>
              : shownTenants.length === 0
              ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    {tenantRows.length ? 'No tenants match.' : 'No tenant logs for this execution.'}
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Tenant</strong></TableCell>
                        <TableCell><strong>Start Time</strong></TableCell>
                        <TableCell><strong>End Time</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Duration</strong></TableCell>
                        <TableCell><strong>Records</strong></TableCell>
                        <TableCell><strong>Error</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shownTenants.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell><Typography variant="body2" fontWeight="medium">{row.tenant_name || '—'}</Typography></TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDatetime(row.start_time)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDatetime(row.end_time)}</TableCell>
                          <TableCell><StatusChip status={row.status} /></TableCell>
                          <TableCell>{row.duration_display || '—'}</TableCell>
                          <TableCell>
                            {row.records_processed != null
                              ? <Chip label={row.records_processed} size="small" color={row.records_processed > 0 ? 'success' : 'default'} variant="outlined" />
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {row.error_message
                              ? <Typography variant="caption" color="error" sx={{ overflowWrap: 'anywhere' }}>{row.error_message}</Typography>
                              : <Typography variant="caption" color="text.secondary">—</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
          </DialogContent>

          <Divider />
          <DialogActions>
            <Button onClick={closeBreakdown} variant="outlined">Close</Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
};

export default CronMonitoringDashboard;
